import { DescriptionOutlined } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import ExplorerGrid, { useExplorerGrid } from "common/ExplorerGrid";
import { enqueueSnackbar } from "notistack";
import useToolbarProps from "qubCommon/ExplorerToolbar/useToolbarProps";
import { DateInput, TextInput } from "qubCommon/Inputs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  bulkReviewTransHeaderExpenses,
  getTransHeaderExpenseReviewPreview,
} from "src/apis/controllers/expenses";
import ConfirmDialog from "src/qubCommon/Dialogs/ConfirmationDialog";
import { getVisibleExpenseRows } from "./visibleExpenseRows";

const TRANSACTIONS_EXPLORER_KEY = "TransHeaderExpenses-Expl";
const REVIEW_RESULT_COLUMN_WIDTH = 170;

const getRowId = (row) => row?.id ?? row?.Id;
const getRowStatusId = (row) => row?.statusId ?? row?.StatusId;
const normalizeRowId = (rowId) => String(rowId ?? "").toLowerCase();

const BULK_ACTIONS = {
  approve: { label: "Έγκριση" },
  reject: { label: "Απόρριψη" },
  return: { label: "Επιστροφή στο Φορέα" },
};

const REVIEW_RESULT_BADGES = {
  approval: { sx: { bgcolor: "#2e7d32", color: "#fff" } },
  partial: { sx: { bgcolor: "#2e7d32", color: "#fff" } },
  mixed: { sx: { bgcolor: "#ed6c02", color: "#fff" } },
  rejection: { sx: { bgcolor: "#d32f2f", color: "#fff" } },
  pending: { sx: { bgcolor: "#e0e0e0", color: "#424242" } },
  not_reviewed: { sx: { bgcolor: "#eeeeee", color: "#616161" } },
  loading: { sx: { bgcolor: "#eeeeee", color: "#757575" } },
  error: { sx: { bgcolor: "#eeeeee", color: "#616161" } },
};

const ExpenseTransactionsExplorer = ({
  applicationId,
  childId,
  canBulkReview = false,
  bulkReviewStatusId,
  bulkReturnRejectStatusIds = [],
  canManageMinutes = false,
}) => {
  const navigate = useNavigate();
  const selectedRowsRef = useRef(new Map());
  const [selectedCount, setSelectedCount] = useState(0);
  const [reviewIndicatorVersion, setReviewIndicatorVersion] = useState(0);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const { status, critParams, ...toolbarRest } = useToolbarProps({
    webPageKey: TRANSACTIONS_EXPLORER_KEY,
    hideAddNew: true,
  });

  const reviewGridColumns = useMemo(() => {
    if (!canBulkReview) return toolbarRest.initColumns ?? [];

    let hasInsertedReviewColumn = false;
    return (toolbarRest.initColumns ?? []).map((columnGroup) => {
      const children = columnGroup?.children;
      if (!Array.isArray(children) || hasInsertedReviewColumn) {
        return columnGroup;
      }

      const statusColumnIndex = children.findIndex(
        (column) => column?.fldName === "StatusAbbr",
      );
      if (statusColumnIndex < 0) return columnGroup;

      hasInsertedReviewColumn = true;
      const nextChildren = [...children];
      nextChildren.splice(statusColumnIndex + 1, 0, {
        fldName: "ReviewResultLabel",
        fldTitle: "ΑΠΟΤ. ΕΛΕΓΧΟΥ",
        colTitle: "ΑΠΟΤ. ΕΛΕΓΧΟΥ",
        colWidth: String(REVIEW_RESULT_COLUMN_WIDTH),
        colAlign: "center",
        isVisible: 1,
        hideFromPicker: 1,
        cellTemplates: [
          {
            cellType: "Cell",
            template: ({ item }) => {
              const resultKey = item?.ReviewResultKey ?? "loading";
              const badge =
                REVIEW_RESULT_BADGES[resultKey] ?? REVIEW_RESULT_BADGES.pending;
              const label = item?.ReviewResultLabel ?? "…";

              if (resultKey === "not_applicable") return "—";

              return (
                <Tooltip title={item?.ReviewResultReason ?? "Υπολογισμός αποτελέσματος"}>
                  <Chip
                    size="small"
                    label={label}
                    sx={{
                      ...badge.sx,
                      height: 24,
                      fontSize: 12,
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Tooltip>
              );
            },
          },
        ],
      });

      return { ...columnGroup, children: nextChildren };
    });
  }, [canBulkReview, toolbarRest.initColumns]);

  const getApplicationCriteria = (criteriaValues = {}) => {
    if (!applicationId) return [];

    const criteria = [
      {
        fldName: "ApplicationId",
        operator: "=",
        value1: applicationId,
      },
    ];

    if (childId) {
      criteria.push({
        fldName: "ChildId",
        operator: "=",
        value1: childId,
      });
    }

    if (criteriaValues?.dateFrom) {
      criteria.push({
        fldName: "TransDate",
        operator: ">=",
        value1: criteriaValues.dateFrom,
      });
    }

    if (criteriaValues?.dateTo) {
      criteria.push({
        fldName: "TransDate",
        operator: "<=",
        value1: criteriaValues.dateTo,
      });
    }

    return criteria;
  };

  const { explParams, ...explorerRest } = useExplorerGrid({
    explorerDataKey: TRANSACTIONS_EXPLORER_KEY,
    xlsxName: "Δαπάνες ΜΗΔ",
    navUrl: `/expenses/${applicationId}/transactions/{:id}`,
    status,
    additionalCritFldsSearch: getApplicationCriteria,
  });

  useEffect(() => {
    let isActive = true;
    const collectionView = explorerRest.gridData;
    const rows = collectionView?.items ?? [];
    const transHeaderIds = [
      ...new Set(rows.map((row) => getRowId(row)).filter((rowId) => !!rowId)),
    ];

    const updateRows = (indicatorsById) => {
      const applyUpdates = () => {
        rows.forEach((row) => {
          const indicator = indicatorsById.get(normalizeRowId(getRowId(row)));
          row.ReviewResultKey = indicator?.resultKey ?? "not_applicable";
          row.ReviewResultLabel = indicator?.resultLabel ?? "—";
          row.ReviewResultReason = indicator?.reason ?? null;
          row.ReviewCanApprove = indicator?.canApprove === true;
        });
      };

      if (typeof collectionView?.deferUpdate === "function") {
        collectionView.deferUpdate(applyUpdates);
      } else {
        applyUpdates();
        collectionView?.refresh?.();
      }
    };

    const enforceReviewColumnWidth = () => {
      const grid = explorerRest.gridRef.current;
      const reviewColumn = grid?.getColumn?.("ReviewResultLabel");
      if (reviewColumn) {
        reviewColumn.minWidth = REVIEW_RESULT_COLUMN_WIDTH;
        reviewColumn.width = Math.max(
          Number(reviewColumn.width) || 0,
          REVIEW_RESULT_COLUMN_WIDTH,
        );
      }
      grid?.invalidate(false);
    };

    setReviewIndicatorVersion((version) => version + 1);
    explorerRest.gridRef.current?.invalidate(false);

    if (!canBulkReview || transHeaderIds.length === 0) {
      return () => {
        isActive = false;
      };
    }

    const fetchReviewPreview = async () => {
      try {
        const res = await getTransHeaderExpenseReviewPreview({ transHeaderIds });
        if (!isActive) return;

        const indicators = Array.isArray(res?.data) ? res.data : [];
        const indicatorsById = new Map(
          indicators.map((indicator) => [
            normalizeRowId(indicator?.transHeaderId),
            indicator,
          ]),
        );
        updateRows(indicatorsById);
      } catch {
        if (!isActive) return;

        const indicatorsById = new Map(
          transHeaderIds.map((transHeaderId) => [
            normalizeRowId(transHeaderId),
            {
              resultKey: "error",
              resultLabel: "Δεν υπολογίστηκε",
              reason: "Δεν ήταν δυνατός ο υπολογισμός του αποτελέσματος ελέγχου",
              canApprove: false,
            },
          ]),
        );
        updateRows(indicatorsById);
      } finally {
        if (isActive) {
          setReviewIndicatorVersion((version) => version + 1);
          enforceReviewColumnWidth();
        }
      }
    };

    fetchReviewPreview();

    return () => {
      isActive = false;
    };
  }, [canBulkReview, explorerRest.gridData, explorerRest.gridRef]);

  useEffect(() => {
    selectedRowsRef.current = new Map();
    setSelectedCount(0);
    setBulkMenuAnchor(null);
    setBulkAction(null);
  }, [explorerRest.gridData]);

  const selectedIds = useMemo(
    () => ({
      has: (rowId) => selectedRowsRef.current.has(rowId),
    }),
    [],
  );

  const refreshSelectionCells = useCallback(() => {
    explorerRest.gridRef.current?.invalidate(false);
  }, [explorerRest.gridRef]);

  const getVisibleRows = useCallback(
    () => getVisibleExpenseRows(explorerRest.gridRef.current?.rows),
    [explorerRest.gridRef],
  );

  const applySelectedRows = useCallback(
    (rows) => {
      selectedRowsRef.current = rows;
      setSelectedCount(rows.size);
      refreshSelectionCells();
    },
    [refreshSelectionCells],
  );

  const keepVisibleSelection = useCallback(() => {
    const visibleIds = new Set(getVisibleRows().map(getRowId));
    const selectedRows = selectedRowsRef.current;
    const visibleSelection = new Map(
      [...selectedRows].filter(([rowId]) => visibleIds.has(rowId)),
    );
    if (visibleSelection.size !== selectedRows.size) {
      applySelectedRows(visibleSelection);
    }
  }, [applySelectedRows, getVisibleRows]);

  const isRowSelectable = useCallback(
    (row) => {
      if (!canBulkReview) return false;

      const statusId = String(getRowStatusId(row));
      return (
        (bulkReviewStatusId != null &&
          statusId === String(bulkReviewStatusId)) ||
        bulkReturnRejectStatusIds.some(
          (allowedStatusId) => statusId === String(allowedStatusId),
        )
      );
    },
    [bulkReturnRejectStatusIds, bulkReviewStatusId, canBulkReview],
  );

  const toggleRow = useCallback((row) => {
    const rowId = getRowId(row);
    if (!rowId || !isRowSelectable(row)) return;

    const selectedRows = selectedRowsRef.current;
    const selectedStatusId = getRowStatusId(
      selectedRows.values().next().value,
    );
    if (
      !selectedRows.has(rowId) &&
      selectedRows.size > 0 &&
      String(selectedStatusId) !== String(getRowStatusId(row))
    ) {
      enqueueSnackbar(
        "Η μαζική ενέργεια εφαρμόζεται σε μία κατάσταση κάθε φορά. Φιλτράρετε πρώτα το ευρετήριο στην επιθυμητή κατάσταση.",
        { variant: "warning" },
      );
      return;
    }

    const next = new Map(selectedRows);
    if (next.has(rowId)) next.delete(rowId);
    else next.set(rowId, row);
    applySelectedRows(next);
  }, [applySelectedRows, isRowSelectable]);

  const toggleAllVisible = useCallback((visibleRows = []) => {
    const selectedRows = selectedRowsRef.current;
    const rowsWithIds = visibleRows.filter(
      (row) => !!getRowId(row) && isRowSelectable(row),
    );
    const areAllSelected =
      rowsWithIds.length > 0 &&
      rowsWithIds.every((row) => selectedRows.has(getRowId(row)));

    if (areAllSelected) {
      applySelectedRows(new Map());
      return;
    }

    const statusIds = new Set(
      rowsWithIds.map((row) => String(getRowStatusId(row))),
    );
    if (statusIds.size > 1) {
      enqueueSnackbar(
        "Για επιλογή όλων, φιλτράρετε πρώτα το grid σε μία κατάσταση.",
        { variant: "warning" },
      );
      return;
    }

    applySelectedRows(
      new Map(rowsWithIds.map((row) => [getRowId(row), row])),
    );
  }, [applySelectedRows, isRowSelectable]);

  const rowSelection = useMemo(
    () => ({
      enabled: canBulkReview,
      selectedIds,
      getRowId,
      getVisibleRows,
      onToggle: toggleRow,
      onToggleAll: toggleAllVisible,
      onVisibleRowsChange: keepVisibleSelection,
      isRowDisabled: (row) => !isRowSelectable(row),
      disabled: isBulkSaving,
    }),
    [
      canBulkReview,
      getVisibleRows,
      isBulkSaving,
      selectedIds,
      isRowSelectable,
      keepVisibleSelection,
      toggleAllVisible,
      toggleRow,
    ],
  );

  const openBulkConfirmation = (action) => {
    setBulkMenuAnchor(null);
    keepVisibleSelection();
    if (selectedRowsRef.current.size === 0) {
      enqueueSnackbar("Επιλέξτε τουλάχιστον μία δαπάνη.", {
        variant: "warning",
      });
      return;
    }

    setBulkAction(action);
  };

  const selectedStatusId = getRowStatusId(
    selectedRowsRef.current.values().next().value,
  );
  const hasReviewPreview = reviewIndicatorVersion > 0;
  const canApproveSelected =
    selectedCount > 0 &&
    bulkReviewStatusId != null &&
    String(selectedStatusId) === String(bulkReviewStatusId) &&
    hasReviewPreview &&
    [...selectedRowsRef.current.values()].every(
      (row) => row?.ReviewCanApprove === true,
    );

  const confirmBulkAction = async () => {
    keepVisibleSelection();
    if (!bulkAction || selectedRowsRef.current.size === 0) return;

    try {
      setIsBulkSaving(true);
      const res = await bulkReviewTransHeaderExpenses({
        transHeaderIds: [...selectedRowsRef.current.keys()],
        action: bulkAction,
      });

      if (res?.status === "error" || res?.succeeded === false) {
        const validationErrors = res?.data?.validationErrors ?? [];
        enqueueSnackbar(
          validationErrors.length > 0
            ? (
              <Box
                component="span"
                sx={{ display: "block", whiteSpace: "pre-line" }}
              >
                {validationErrors.join("\n")}
              </Box>
            )
            : res?.message ?? "Δεν ολοκληρώθηκε η μαζική αλλαγή κατάστασης.",
          { variant: "error" },
        );
        return;
      }

      enqueueSnackbar(
        res?.message ?? "Η μαζική αλλαγή κατάστασης ολοκληρώθηκε.",
        { variant: "success" },
      );
      await explorerRest.handleSubmit(explorerRest.onSubmit)();
      applySelectedRows(new Map());
      setBulkAction(null);
    } catch (error) {
      enqueueSnackbar(
        error?.message ?? "Προέκυψε σφάλμα στη μαζική αλλαγή κατάστασης.",
        { variant: "error" },
      );
    } finally {
      setIsBulkSaving(false);
    }
  };

  const toolbarButtons = (
    <Stack direction="row" spacing={1} alignItems="center">
      {canManageMinutes && (
        <Box boxShadow={2} sx={{ borderRadius: 50 }}>
          <Tooltip title="Πρακτικά">
            <IconButton
              aria-label="Πρακτικά"
              onClick={() => navigate(`/expenses/${applicationId}/minutes`)}
            >
              <DescriptionOutlined />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {canBulkReview && (
        <>
          <Button
            size="small"
            variant="contained"
            disabled={selectedCount === 0 || isBulkSaving}
            onClick={(event) => setBulkMenuAnchor(event.currentTarget)}
          >
            Μαζική αλλαγή ({selectedCount})
          </Button>
          <Menu
            anchorEl={bulkMenuAnchor}
            open={Boolean(bulkMenuAnchor)}
            onClose={() => setBulkMenuAnchor(null)}
          >
            {Object.entries(BULK_ACTIONS)
              .filter(([action]) => action !== "approve" || canApproveSelected)
              .map(([action, config]) => (
                <MenuItem
                  key={action}
                  onClick={() => openBulkConfirmation(action)}
                >
                  {config.label}
                </MenuItem>
              ))}
          </Menu>
        </>
      )}
    </Stack>
  );

  return (
    <>
      <Backdrop
        open={isBulkSaving}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <CircularProgress size={70} />
      </Backdrop>
      <Box
        sx={{
          border: "1px solid rgba(0, 0, 0, 0.125)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          overflow: "hidden",
          p: 1.5,
        }}
      >
        <Box>
          <ExplorerGrid
            explParams={explParams}
            title="Αναλυτικές Δαπάνες"
            breadcrumbs={[{ name: "Αναλυτικές Δαπάνες", path: "expenses" }]}
            autoRowHeights
            stickyHeaders
            submitFiltersOnEnter
            {...explorerRest}
            {...toolbarRest}
            initColumns={reviewGridColumns}
            buttons={toolbarButtons}
            rowSelection={rowSelection}
          >
            <Grid item xs={12} md={6} lg={3}>
              <TextInput
                id="Search"
                name="searchFieldValue"
                label="Αναζήτηση"
                defaultValue={critParams?.searchFieldValue ?? ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <DateInput
                fixedPadding={8}
                name="dateFrom"
                label="Από Ημ/νία"
                defaultValue={critParams?.dateFrom ?? ""}
                isFullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3} lg={2}>
              <DateInput
                fixedPadding={8}
                name="dateTo"
                label="Έως Ημ/νία"
                defaultValue={critParams?.dateTo ?? ""}
                isFullWidth
              />
            </Grid>
          </ExplorerGrid>
        </Box>
        <ConfirmDialog
          open={Boolean(bulkAction)}
          title={bulkAction ? BULK_ACTIONS[bulkAction]?.label : ""}
          content={`Θέλετε να εφαρμοστεί η ενέργεια σε ${selectedCount} επιλεγμένες δαπάνες;`}
          onClose={() => !isBulkSaving && setBulkAction(null)}
          onConfirm={confirmBulkAction}
        />
      </Box>
    </>
  );
};

export default ExpenseTransactionsExplorer;
